import { Component, OnInit, OnDestroy } from '@angular/core';
import {truncateDecimals} from '../../Custom-Directives-Pipes/truncate-decimals.pipe';
import { FormControl } from '@angular/forms';
import { Subscription, catchError, throwError, forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DraftConfirmationDialogComponent } from 'src/app/dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component';
import { SeasonFarmsModel } from 'src/app/models/season-models/season-farms-model';
import { SeasonFieldsModel } from 'src/app/models/season-models/season-fields-model';
import { SeasonCropModel } from 'src/app/models/season-models/season-crop-model';
import { SeasonTypeModel } from 'src/app/models/season-models/season-field-type-model';
import { SeasonIrrigationTypeModel } from 'src/app/models/season-models/season-irrigation-type-model';
import { SeasonTillageModel } from 'src/app/models/season-models/season-tillage-model';
import { SeasonPodEffectivenessModel } from 'src/app/models/season-models/season-pod-effectiveness-model';
import { SeasonHarvestResiduesModel } from 'src/app/models/season-models/season-harvest-residues-model';
import { SeasonPlantActionModel } from 'src/app/models/season-models/season-plant-action-model';
import { SeasonFertilizerPlacementModel } from 'src/app/models/season-models/season-fertilizer-placement';
import { SeasonPlantFertilizerTypeModel } from 'src/app/models/season-models/season-plant-fertilizer-type-model';
import { SeasonTopDressingMethodModel } from 'src/app/models/season-models/season-top-dressing-method-model';
import { SeasonTypeOfFertilizerBobModel } from 'src/app/models/season-models/season-type-of-fertilizer-bob-model';
import { SeasonFertilizerTypeModel } from 'src/app/models/season-models/season-fertilizer-type-model';
import { SeasonFertilizerPlacementWithPlantingModel } from 'src/app/models/season-models/season-fertilizer-placement-with-planting-model';
import { SeasonMethodOfTopDressingModel } from 'src/app/models/season-models/season-method-of-top-dressing-model';
import { SeasonTypeOfTopDressingFertilizer } from 'src/app/models/season-models/season-type-of-top-dressing-fertilizer-model';
import { SeasonWaterQualityEcModel } from 'src/app/models/season-models/season-water-quality-ec-model';
import { SeasonFieldPlantModel } from 'src/app/models/season-models/season-field-plant-model';
import { ExstraDetailModel } from 'src/app/models/season-models/exstra-detail-model';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { SeasonSharedDataService } from 'src/app/services/season-shared-data.service';
import { SeasonFieldBaseDto } from 'src/app/dto/season-dto/season-field-base-dto';
import { CreateSamplesDialogComponent } from 'src/app/dialogs/season-manager-dialogs/create-samples-dialog/create-samples-dialog.component';
import { BatchCreateSamplesDataService } from '../batch-create-samples/batch-create-samples-data.service';
import { Router, ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';

export interface TableRow {
  field: SeasonFieldsModel;
  farmName: string;
  extraDetail: ExstraDetailModel | null;
  dirty: boolean;
  /** Set when this row's cultivar size is negative or has more than 2 decimals. */
  cultivarSizeInvalid?: boolean;
  /**
   * Set after a blocked save for any row involved in a cultivar size problem.
   * Covers the sum rule as well as the format rule, so every definition on an
   * over-allocated field is highlighted, not just one of them.
   */
  cultivarSizeRowInvalid?: boolean;
  definitionCount?: number;
  isFirstDefinition?: boolean;
  isLastDefinition?: boolean;
  definitionId?: number;
  fieldGroupId?: number;
}

@Component({
  selector: 'app-season-data-table',
  templateUrl: './season-data-table.component.html',
  styleUrls: ['./season-data-table.component.css'],
  standalone: false
})
export class SeasonDataTableComponent implements OnInit, OnDestroy {

  seasonId: number = 0;
  allRows: TableRow[] = [];

  // Metadata dropdowns
  fieldTypes: SeasonTypeModel[] = [];
  crops: SeasonCropModel[] = [];
  irrigationTypes: SeasonIrrigationTypeModel[] = [];
  tillage: SeasonTillageModel[] = [];
  podEffectiveness: SeasonPodEffectivenessModel[] = [];
  harvestResidues: SeasonHarvestResiduesModel[] = [];
  plantAction: SeasonPlantActionModel[] = [];
  fertilizerPlacement: SeasonFertilizerPlacementModel[] = [];
  plantFertilizerType: SeasonPlantFertilizerTypeModel[] = [];
  plantSpread: SeasonFieldPlantModel[] = [];
  topDressingMethod: SeasonTopDressingMethodModel[] = [];
  typeOfFertilizerBob: SeasonTypeOfFertilizerBobModel[] = [];
  fertilizerType: SeasonFertilizerTypeModel[] = [];
  fertilizerPlacementWithPlanting: SeasonFertilizerPlacementWithPlantingModel[] = [];
  methodOfTopDressing: SeasonMethodOfTopDressingModel[] = [];
  typeOfTopDressingFertilizer: SeasonTypeOfTopDressingFertilizer[] = [];
  waterQualityEc: SeasonWaterQualityEcModel[] = [];

  isLoading = true;
  searchQuery: string = '';
  selectedRows: Set<number> = new Set(); // tracks field ids

  // Column filters
  filters: { [key: string]: any } = {};

  // Search controls for searchable dropdowns
  farmSearchCtrl = new FormControl('');
  cropSearchCtrl = new FormControl('');
  irrigationSearchCtrl = new FormControl('');
  filteredCrops: SeasonCropModel[] = [];
  filteredIrrigationTypes: SeasonIrrigationTypeModel[] = [];
  filteredFarmNames: string[] = [];

  private subs = new Subscription();

  constructor(
    public nav: NavBarComponent,
    private seasonManagerService: SeasonManagerService,
    private seasonSharedDataService: SeasonSharedDataService,
    private matDialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private batchDataService: BatchCreateSamplesDataService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.seasonSharedDataService.seasonIdData$.subscribe(id => {
        if (id) {
          this.seasonId = id;
          this.loadData();
        }
      })
    );
    this.loadMetaData();
    this.initSearchControls();
    this.seasonSharedDataService.setIsSaveBtnVisibleDataSubjectData(false);
    this.seasonSharedDataService.updateIsHiddenData(false);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadMetaData(): void {
    this.subs.add(
      this.seasonSharedDataService.combinedData$.subscribe(data => {
        if (data) {
          this.fieldTypes = data.seasonFieldType || [];
          this.crops = data.seasonFieldCrop || [];
          this.irrigationTypes = data.seasonFieldIrrigationType || [];
          this.tillage = data.seasonFieldTillage || [];
          this.podEffectiveness = data.seasonFieldPodEffectiveness || [];
          this.harvestResidues = data.seasonFieldHarvestResidues || [];
          this.plantAction = data.seasonFieldPlantAction || [];
          this.fertilizerPlacement = data.seasonFieldFertilizerPlacement || [];
          this.plantFertilizerType = data.seasonFieldPlantFertilizerType || [];
          this.plantSpread = data.seasonFieldPlant || [];
          this.topDressingMethod = data.seasonFieldTopDressingMethod || [];
          this.typeOfFertilizerBob = data.seasonFieldTypeOfFertilizerBob || [];
          this.fertilizerType = data.seasonFieldFertilizerType || [];
          this.fertilizerPlacementWithPlanting = data.seasonFieldFertilizerPlacementWithPlanting || [];
          this.methodOfTopDressing = data.seasonFieldMethodOfTopDressing || [];
          this.typeOfTopDressingFertilizer = data.seasonFieldTypeOfTopDressingFertilizer || [];
          this.waterQualityEc = data.seasonFieldWaterQualityEc || [];
          this.refreshFilteredLists();
        }
      })
    );
  }

  loadData(): void {
    this.isLoading = true;
    this.nav.toggleLoading(true);

    this.seasonManagerService.getSeasonById(this.seasonId, true).pipe(
      catchError(error => {
        this.isLoading = false;
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe(farms => {
      this.buildRows(farms);
    });
  }

  private buildRows(farms: SeasonFarmsModel[]): void {
    this.allRows = [];
    const extraCalls: { rowIdx: number; obs: any }[] = [];

    farms.forEach(farm => {
      farm.fields.forEach(field => {
        const row: TableRow = {
          field: { ...field },
          farmName: farm.name,
          extraDetail: null,
          dirty: false,
          definitionId: field.id,
          fieldGroupId: field.fieldId,
        };
        const rowIdx = this.allRows.length;
        this.allRows.push(row);

        if (field.fieldType) {
          extraCalls.push({
            rowIdx,
            obs: this.seasonManagerService.getSeasonDetailExtraData(field.id, Number(field.fieldType))
          });
        }
      });
    });

    // Set multi-definition flags by grouping on fieldId
    this.setDefinitionFlags();

    if (extraCalls.length === 0) {
      this.isLoading = false;
      this.nav.toggleLoading(false);
      this.updateFilteredRows();
      return;
    }

    forkJoin(extraCalls.map(c => c.obs.pipe(catchError(() => [null])))).subscribe(results => {
      results.forEach((result, i) => {
        if (result) {
          this.allRows[extraCalls[i].rowIdx].extraDetail = result;
          const row = this.allRows[extraCalls[i].rowIdx];
          if (row.field.fieldType === 1 && !row.extraDetail.grainFeed) {
            row.extraDetail.grainFeed = { previousCropId: null, tillageId: null, bearing: null, podEffectivenessId: null, harvestResiduesId: null, plantId: null, plantActionId: null, trackWidth: null, fertilizerPlacementId: null, plantFertilizerTypeId: null, topDressingMethodId: null, typeOfFertilizerBob1Id: null, typeOfFertilizerBob2Id: null };
          }
          if (row.field.fieldType === 2 && !row.extraDetail.vegetables) {
            row.extraDetail.vegetables = { previousCropId: null, tillageId: null, harvestResiduesId: null, plantActionId: null, plantDensity: null, fertilizerPlacementWithPlantingId: null, fertilizerTypeId: null, methodOfTopDressingId: null, typeOfTopDressingFertilizerId: null, waterQualityEcId: null, timeLimeApplied: null, thaLimeApplied: null };
          }
          if (row.field.fieldType === 3 && !row.extraDetail.vineyard) {
            row.extraDetail.vineyard = { growthPower: null, fertilizerTypeId: null, plantYear: null };
          }
          if (row.field.fieldType === 4 && !row.extraDetail.fruit) {
            row.extraDetail.fruit = { growthPower: null, fertilizerTypeId: null, plantYear: null };
          }
        }
      });
      this.isLoading = false;
      this.nav.toggleLoading(false);
      this.updateFilteredRows();
      this.refreshFilteredLists();
    });
  }

  private setDefinitionFlags(): void {
    const fieldGroups: { [key: number]: TableRow[] } = {};
    this.allRows.forEach(row => {
      const fid = row.fieldGroupId || row.field.fieldId;
      if (!fieldGroups[fid]) fieldGroups[fid] = [];
      fieldGroups[fid].push(row);
    });
    Object.values(fieldGroups).forEach(group => {
      group.forEach((row, i) => {
        row.definitionCount = group.length;
        row.isFirstDefinition = i === 0;
        row.isLastDefinition = i === group.length - 1;
      });
    });
  }

  // --- Grouped row arrays (stored, updated explicitly) ---

  unassignedRows: TableRow[] = [];
  grainFeedRows: TableRow[] = [];
  vegetableRows: TableRow[] = [];
  vineyardRows: TableRow[] = [];
  fruitRows: TableRow[] = [];

  updateFilteredRows(): void {
    this.unassignedRows = this.sortRows(this.filterBySearch(this.allRows.filter(r => !r.field.fieldType)));
    this.grainFeedRows = this.sortRows(this.filterBySearch(this.allRows.filter(r => r.field.fieldType === 1)));
    this.vegetableRows = this.sortRows(this.filterBySearch(this.allRows.filter(r => r.field.fieldType === 2)));
    this.vineyardRows = this.sortRows(this.filterBySearch(this.allRows.filter(r => r.field.fieldType === 3)));
    this.fruitRows = this.sortRows(this.filterBySearch(this.allRows.filter(r => r.field.fieldType === 4)));
  }

  // --- Grouped getters ---

  private filterBySearch(rows: TableRow[]): TableRow[] {
    let result = rows;

    // Text search
    if (this.searchQuery?.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      result = result.filter(r =>
        r.field.fieldName?.toLowerCase().includes(q) ||
        r.farmName?.toLowerCase().includes(q)
      );
    }

    // Column filters
    const f = this.filters;

    if (f['farmName']) {
      result = result.filter(r => r.farmName === f['farmName']);
    }
    if (f['fieldName']) {
      const q = f['fieldName'].toLowerCase();
      result = result.filter(r => r.field.fieldName?.toLowerCase().includes(q));
    }
    if (f['currentCrop'] !== undefined && f['currentCrop'] !== null) {
      result = result.filter(r => r.field.currentCrop === f['currentCrop']);
    }
    if (f['irrigationType'] !== undefined && f['irrigationType'] !== null) {
      result = result.filter(r => r.field.irrigationType === f['irrigationType']);
    }
    if (f['isIrrigation'] !== undefined && f['isIrrigation'] !== null) {
      const isIrr = f['isIrrigation'];
      result = result.filter(r => !!(r.field.isIrrigation) === isIrr);
    }
    if (f['isHarvested'] !== undefined && f['isHarvested'] !== null) {
      result = result.filter(r => r.field.isHarvested === f['isHarvested']);
    }
    if (f['allowNotifications'] !== undefined && f['allowNotifications'] !== null) {
      result = result.filter(r => r.field.allowNotifications === f['allowNotifications']);
    }

    return result;
  }

  getUniqueFarmNames(): string[] {
    const names = new Set<string>();
    this.allRows.forEach(r => { if (r.farmName) names.add(r.farmName); });
    return Array.from(names).sort();
  }

  private initSearchControls(): void {
    this.farmSearchCtrl.valueChanges.subscribe(val => {
      const q = (val || '').toLowerCase();
      this.filteredFarmNames = this.getUniqueFarmNames().filter(n => n.toLowerCase().includes(q));
    });
    this.cropSearchCtrl.valueChanges.subscribe(val => {
      const q = (val || '').toLowerCase();
      this.filteredCrops = this.crops.filter(c => c.description?.toLowerCase().includes(q));
    });
    this.irrigationSearchCtrl.valueChanges.subscribe(val => {
      const q = (val || '').toLowerCase();
      this.filteredIrrigationTypes = this.irrigationTypes.filter(i => i.description?.toLowerCase().includes(q));
    });
  }

  refreshFilteredLists(): void {
    this.filteredFarmNames = this.getUniqueFarmNames();
    this.filteredCrops = [...this.crops];
    this.filteredIrrigationTypes = [...this.irrigationTypes];
  }

  clearFilters(): void {
    this.filters = {};
    this.searchQuery = '';
  }

  // Section collapse
  collapsedSections: Set<string> = new Set();

  toggleSection(key: string): void {
    if (this.collapsedSections.has(key)) {
      this.collapsedSections.delete(key);
    } else {
      this.collapsedSections.add(key);
    }
  }

  isSectionCollapsed(key: string): boolean {
    return this.collapsedSections.has(key);
  }

  get hasActiveFilters(): boolean {
    return !!this.searchQuery?.trim() || Object.keys(this.filters).some(k => this.filters[k] !== null && this.filters[k] !== undefined && this.filters[k] !== '');
  }

  private sortRows(rows: TableRow[]): TableRow[] {
    return rows.sort((a, b) => {
      const farmCompare = (a.farmName || "").localeCompare(b.farmName || "", undefined, { numeric: true, sensitivity: "base" });
      if (farmCompare !== 0) return farmCompare;
      return (a.field.fieldName || "").localeCompare(b.field.fieldName || "", undefined, { numeric: true, sensitivity: "base" });
    });
  }

  // --- Cell change ---

  onCellChange(row: TableRow): void {
    row.dirty = true;
    // Flag the cell straight away so the user sees the problem while typing,
    // rather than only when they try to save.
    row.cultivarSizeInvalid = this.cultivarSizeFormatError(row.field.cultivarSize) !== null;

    // Once a save has been blocked, keep the banner and the row highlighting in
    // step with what the user is typing. The sum rule spans rows, so editing
    // one cell can clear or create a problem on its siblings.
    if (this.saveAttempted) {
      this.cultivarSizeErrors = this.validateCultivarSizes().errors;
    }
  }

  onFieldTypeChange(row: TableRow, fieldTypeId: number | null): void {
    row.field.fieldType = fieldTypeId;
    row.dirty = true;

    if (!fieldTypeId) {
      row.extraDetail = null;
      return;
    }

    if (!row.extraDetail) {
      row.extraDetail = { grainFeed: null, vegetables: null, vineyard: null, fruit: null };
    }

    if (fieldTypeId === 1 && !row.extraDetail.grainFeed) {
      row.extraDetail.grainFeed = {
        previousCropId: null, tillageId: null, bearing: null, podEffectivenessId: null,
        harvestResiduesId: null, plantId: null, plantActionId: null, trackWidth: null,
        fertilizerPlacementId: null, plantFertilizerTypeId: null, topDressingMethodId: null,
        typeOfFertilizerBob1Id: null, typeOfFertilizerBob2Id: null
      };
    } else if (fieldTypeId === 2 && !row.extraDetail.vegetables) {
      row.extraDetail.vegetables = {
        previousCropId: null, tillageId: null, harvestResiduesId: null, plantActionId: null,
        plantDensity: null, fertilizerPlacementWithPlantingId: null, fertilizerTypeId: null,
        methodOfTopDressingId: null, typeOfTopDressingFertilizerId: null, waterQualityEcId: null,
        timeLimeApplied: null, thaLimeApplied: null
      };
    } else if (fieldTypeId === 3 && !row.extraDetail.vineyard) {
      row.extraDetail.vineyard = { growthPower: null, fertilizerTypeId: null, plantYear: null };
    } else if (fieldTypeId === 4 && !row.extraDetail.fruit) {
      row.extraDetail.fruit = { growthPower: null, fertilizerTypeId: null, plantYear: null };
    }
    this.updateFilteredRows();
  }

  // --- Save ---

  get hasDirtyRows(): boolean {
    return this.allRows.some(r => r.dirty);
  }

  /** Populated when a save is blocked; one entry per offending field. */
  cultivarSizeErrors: string[] = [];

  /** Set when a batch apply is rejected because its cultivar size is malformed. */
  batchCultivarSizeError: string | null = null;

  /**
   * True once a save has been blocked. Row highlighting only appears after the
   * user has actually tried to save, but from then on it re-evaluates on every
   * edit so the red clears as they fix things rather than sticking until the
   * next save attempt.
   */
  private saveAttempted = false;

  /** Counts behind cultivarSizeSummary. Set by validateCultivarSizes. */
  cultivarSizeFormatCount = 0;
  cultivarSizeSumCount = 0;

  /**
   * Format check for a single cultivar size value: non-negative, at most 2
   * decimal places.
   *
   * The admin side panel gets this from Validators.min and Validators.pattern
   * on a reactive control. The data table's cells are plain ngModel bindings,
   * so the same two rules are applied here by hand to keep both surfaces
   * consistent. min="0" and step="0.01" on the input are only browser hints -
   * neither blocks a typed or pasted value.
   *
   * Returns a human-readable fragment describing the problem, or null when the
   * value is acceptable. Empty values are acceptable: the field is optional.
   */
  private cultivarSizeFormatError(value: any): string | null {
    if (value == null || value === '') return null;

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 'is not a valid number';
    if (parsed < 0) return 'cannot be negative';

    const decimals = String(value).split('.')[1];
    if (decimals && decimals.length > 2) return 'has more than 2 decimal places';

    return null;
  }

  /**
   * Checks that each field's crop definitions do not collectively exceed the
   * field's own size.
   *
   * Runs across ALL rows, not just dirty ones - an untouched sibling definition
   * still contributes to its field's total. Grouping is strictly by fieldId,
   * because one field's definitions can appear in different crop-type sections.
   */
  validateCultivarSizes(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const totals = new Map<number, { total: number; fieldSize: number; fieldName: string }>();

    // Format pass. Runs over every row rather than only the dirty ones, so a
    // bad value that arrived from the API is caught too.
    let formatCount = 0;
    for (const row of this.allRows) {
      const problem = this.cultivarSizeFormatError(row.field.cultivarSize);
      row.cultivarSizeInvalid = problem !== null;
      if (problem) {
        formatCount++;
        errors.push(`"${row.field.fieldName}": Cultivar size ${row.field.cultivarSize} ${problem}`);
      }
    }

    for (const row of this.allRows) {
      const f = row.field;
      if (f.fieldId == null) continue;

      // fieldSize is typed as a string on SeasonFieldsModel - parse before use.
      const fieldSize = parseFloat(f.fieldSize as any);
      const cultivarSize = Number(f.cultivarSize);

      const entry = totals.get(f.fieldId) ?? { total: 0, fieldSize, fieldName: f.fieldName };
      entry.total += Number.isFinite(cultivarSize) ? cultivarSize : 0;
      totals.set(f.fieldId, entry);
    }

    const offendingFieldIds = new Set<number>();

    totals.forEach((entry, fieldId) => {
      // Skip fields with no recorded size - there is nothing to validate against.
      if (!Number.isFinite(entry.fieldSize) || entry.fieldSize <= 0) return;
      if (entry.total > entry.fieldSize) {
        offendingFieldIds.add(fieldId);
        errors.push(
          `"${entry.fieldName}": Combined cultivar size ${truncateDecimals(entry.total, 2)} ha ` +
          `exceeds field size of ${truncateDecimals(entry.fieldSize, 2)} ha`
        );
      }
    });

    // Row highlighting. The sum rule is per field, not per row, so every
    // definition on an over-allocated field is marked - between them they are
    // the problem, and there is no way to say which one is wrong.
    for (const row of this.allRows) {
      row.cultivarSizeRowInvalid =
        row.cultivarSizeInvalid ||
        (row.field.fieldId != null && offendingFieldIds.has(row.field.fieldId));
    }

    this.cultivarSizeFormatCount = formatCount;
    this.cultivarSizeSumCount = offendingFieldIds.size;

    return { valid: errors.length === 0, errors };
  }

  /**
   * One-line reason the save is blocked, for the unsaved-changes bar. Counts
   * only - the detailed banner and the row highlighting say which fields.
   */
  get cultivarSizeSummary(): string | null {
    const parts: string[] = [];

    if (this.cultivarSizeSumCount > 0) {
      parts.push(this.cultivarSizeSumCount === 1
        ? '1 field is over its total size'
        : `${this.cultivarSizeSumCount} fields are over their total size`);
    }

    if (this.cultivarSizeFormatCount > 0) {
      parts.push(this.cultivarSizeFormatCount === 1
        ? '1 value is not a valid size'
        : `${this.cultivarSizeFormatCount} values are not valid sizes`);
    }

    if (parts.length === 0) return null;
    return `Cannot save: ${parts.join(' and ')}. See the highlighted rows.`;
  }

  saveAll(): void {
    // Validate before anything is dispatched, so no HTTP request is made.
    this.saveAttempted = true;
    const validation = this.validateCultivarSizes();
    this.cultivarSizeErrors = validation.errors;
    if (!validation.valid) return;

    this.saveAttempted = false;
    this.allRows.forEach(r => r.cultivarSizeRowInvalid = false);

    const dirtyRows: TableRow[] = [];
    this.allRows.forEach(r => { if (r.dirty) dirtyRows.push(r); });
    if (dirtyRows.length === 0) return;

    this.nav.toggleLoading(true);
    let completed = 0;
    const total = dirtyRows.length;

    for (const row of dirtyRows) {
      const f = row.field;
      const baseDto: SeasonFieldBaseDto = {
        fieldTypeId: f.fieldType !== undefined ? f.fieldType : null,
        allowNotifications: f.allowNotifications,
        plantDate: f.plantDate ? String(dayjs(f.plantDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ')) : null,
        currentCropId: f.currentCrop,
        cultivar: f.cultivar,
        cultivarSize: f.cultivarSize != null && (f.cultivarSize as any) !== '' ? Number(f.cultivarSize) : null,
        notes: f.notes != null && f.notes !== '' ? f.notes : null,
        isHarvested: f.isHarvested,
        yieldAverage: f.yieldAverage != null ? Number(f.yieldAverage) : null,
        yieldGoal: f.yieldGoal != null ? Number(f.yieldGoal) : null,
        isIrrigation: f.isIrrigation as any === true || f.isIrrigation as any === 'true',
        expectedWaterAmount: f.expectedWaterAmount != null ? Number(f.expectedWaterAmount) : null,
        irrigationTypeId: f.irrigationType,
      };

      this.seasonManagerService.updateSeasonFieldBaseDetails(f.id, baseDto).subscribe({
        next: () => { if (!f.fieldType) { completed++; if (completed === total) this.onSaveComplete(); } },
        error: () => { completed++; if (completed === total) this.onSaveComplete(); }
      });

      if (f.fieldType && row.extraDetail) {
        const extra = row.extraDetail;
        if (f.fieldType === 1 && extra.grainFeed) {
          this.seasonManagerService.postSeasonDetailsGrainFeed(f.id, extra.grainFeed).subscribe({
            complete: () => { completed++; if (completed === total) this.onSaveComplete(); },
            error: () => { completed++; if (completed === total) this.onSaveComplete(); }
          });
        } else if (f.fieldType === 2 && extra.vegetables) {
          const vegPayload = { ...extra.vegetables, thaLimeApplied: (extra.vegetables as any).tHaLimeApplied };
          this.seasonManagerService.postSeasonDetailsVegetables(f.id, vegPayload).subscribe({
            complete: () => { completed++; if (completed === total) this.onSaveComplete(); },
            error: () => { completed++; if (completed === total) this.onSaveComplete(); }
          });
        } else if (f.fieldType === 3 && extra.vineyard) {
          this.seasonManagerService.postSeasonDetailsVineyard(f.id, extra.vineyard).subscribe({
            complete: () => { completed++; if (completed === total) this.onSaveComplete(); },
            error: () => { completed++; if (completed === total) this.onSaveComplete(); }
          });
        } else if (f.fieldType === 4 && extra.fruit) {
          this.seasonManagerService.postSeasonDetailsFruit(f.id, extra.fruit).subscribe({
            complete: () => { completed++; if (completed === total) this.onSaveComplete(); },
            error: () => { completed++; if (completed === total) this.onSaveComplete(); }
          });
        } else {
          completed++;
          if (completed === total) this.onSaveComplete();
        }
      }
    }
  }

  private onSaveComplete(): void {
    this.allRows.forEach(r => r.dirty = false);
    this.nav.toggleLoading(false);
    this.loadData();
  }

  trackByRow(_: number, item: TableRow): number {
    return item.field.id;
  }

  // --- Selection ---
  toggleRowSelection(row: TableRow): void {
    if (this.selectedRows.has(row.field.id)) {
      this.selectedRows.delete(row.field.id);
    } else {
      this.selectedRows.add(row.field.id);
    }
    this.selectedRows = new Set(this.selectedRows);  }

  isRowSelected(row: TableRow): boolean {
    return this.selectedRows.has(row.field.id);
  }

  toggleSelectAll(rows: TableRow[]): void {
    const allSelected = rows.every(r => this.selectedRows.has(r.field.id));
    if (allSelected) {
      rows.forEach(r => this.selectedRows.delete(r.field.id));
    } else {
      rows.forEach(r => this.selectedRows.add(r.field.id));
    }
    this.selectedRows = new Set(this.selectedRows);  }

  areAllSelected(rows: TableRow[]): boolean {
    return rows.length > 0 && rows.every(r => this.selectedRows.has(r.field.id));
  }

  areSomeSelected(rows: TableRow[]): boolean {
    return rows.some(r => this.selectedRows.has(r.field.id)) && !this.areAllSelected(rows);
  }

  getSelectedRows(): TableRow[] {
    return this.allRows.filter(r => this.selectedRows.has(r.field.id));
  }

  // --- Batch Edit ---
  batchValues: { [key: string]: any } = {};

  hasSelectedInTable(rows: TableRow[]): boolean {
    return rows.some(r => this.selectedRows.has(r.field.id));
  }

  getSelectedCountForTable(rows: TableRow[]): number {
    return rows.filter(r => this.selectedRows.has(r.field.id)).length;
  }
  applyBatchToTable(rows: TableRow[]): void {
    const selected = rows.filter(r => this.selectedRows.has(r.field.id));
    const b = this.batchValues;

    // Reject the whole batch rather than applying every other column and
    // silently dropping the cultivar size.
    this.batchCultivarSizeError = null;
    const batchProblem = this.cultivarSizeFormatError(b['cultivarSize']);
    if (batchProblem) {
      this.batchCultivarSizeError =
        `Batch not applied: cultivar size ${b['cultivarSize']} ${batchProblem}`;
      return;
    }

    for (const row of selected) {
      if (b['fieldType'] !== undefined && b['fieldType'] !== null) {
        this.onFieldTypeChange(row, b['fieldType']);
      }
      if (b['currentCrop'] !== undefined && b['currentCrop'] !== null) row.field.currentCrop = b['currentCrop'];
      if (b['cultivar'] !== undefined && b['cultivar'] !== '') row.field.cultivar = b['cultivar'];
      if (b['cultivarSize'] !== undefined && b['cultivarSize'] !== null && b['cultivarSize'] !== '') row.field.cultivarSize = Number(b['cultivarSize']);
      if (b['notes'] !== undefined && b['notes'] !== '') row.field.notes = b['notes'];
      if (b['plantDate'] !== undefined && b['plantDate'] !== null) row.field.plantDate = b['plantDate'];
      if (b['yieldAverage'] !== undefined && b['yieldAverage'] !== null && b['yieldAverage'] !== '') row.field.yieldAverage = String(b['yieldAverage']);
      if (b['yieldGoal'] !== undefined && b['yieldGoal'] !== null && b['yieldGoal'] !== '') row.field.yieldGoal = String(b['yieldGoal']);
      if (b['isIrrigation'] !== undefined && b['isIrrigation'] !== null) row.field.isIrrigation = b['isIrrigation'];
      if (b['irrigationType'] !== undefined && b['irrigationType'] !== null) row.field.irrigationType = b['irrigationType'];
      if (b['expectedWaterAmount'] !== undefined && b['expectedWaterAmount'] !== null && b['expectedWaterAmount'] !== '') row.field.expectedWaterAmount = Number(b['expectedWaterAmount']);
      if (b['isHarvested'] !== undefined && b['isHarvested'] !== null) row.field.isHarvested = b['isHarvested'];
      if (b['allowNotifications'] !== undefined && b['allowNotifications'] !== null) row.field.allowNotifications = b['allowNotifications'];

      // Grain & Feed extras
      if (row.field.fieldType === 1 && row.extraDetail?.grainFeed) {
        const gf = row.extraDetail.grainFeed;
        if (b['gf_previousCropId'] != null) gf.previousCropId = b['gf_previousCropId'];
        if (b['gf_tillageId'] != null) gf.tillageId = b['gf_tillageId'];
        if (b['gf_bearing'] != null && b['gf_bearing'] !== '') gf.bearing = Number(b['gf_bearing']);
        if (b['gf_podEffectivenessId'] != null) gf.podEffectivenessId = b['gf_podEffectivenessId'];
        if (b['gf_harvestResiduesId'] != null) gf.harvestResiduesId = b['gf_harvestResiduesId'];
        if (b['gf_plantId'] != null) gf.plantId = b['gf_plantId'];
        if (b['gf_plantActionId'] != null) gf.plantActionId = b['gf_plantActionId'];
        if (b['gf_trackWidth'] != null && b['gf_trackWidth'] !== '') gf.trackWidth = Number(b['gf_trackWidth']);
        if (b['gf_fertilizerPlacementId'] != null) gf.fertilizerPlacementId = b['gf_fertilizerPlacementId'];
        if (b['gf_plantFertilizerTypeId'] != null) gf.plantFertilizerTypeId = b['gf_plantFertilizerTypeId'];
        if (b['gf_topDressingMethodId'] != null) gf.topDressingMethodId = b['gf_topDressingMethodId'];
        if (b['gf_typeOfFertilizerBob1Id'] != null) gf.typeOfFertilizerBob1Id = b['gf_typeOfFertilizerBob1Id'];
        if (b['gf_typeOfFertilizerBob2Id'] != null) gf.typeOfFertilizerBob2Id = b['gf_typeOfFertilizerBob2Id'];
      }

      // Vegetables extras
      if (row.field.fieldType === 2 && row.extraDetail?.vegetables) {
        const vg = row.extraDetail.vegetables;
        if (b['vg_previousCropId'] != null) vg.previousCropId = b['vg_previousCropId'];
        if (b['vg_tillageId'] != null) vg.tillageId = b['vg_tillageId'];
        if (b['vg_harvestResiduesId'] != null) vg.harvestResiduesId = b['vg_harvestResiduesId'];
        if (b['vg_plantActionId'] != null) vg.plantActionId = b['vg_plantActionId'];
        if (b['vg_plantDensity'] != null && b['vg_plantDensity'] !== '') vg.plantDensity = Number(b['vg_plantDensity']);
        if (b['vg_fertilizerPlacementWithPlantingId'] != null) vg.fertilizerPlacementWithPlantingId = b['vg_fertilizerPlacementWithPlantingId'];
        if (b['vg_fertilizerTypeId'] != null) vg.fertilizerTypeId = b['vg_fertilizerTypeId'];
        if (b['vg_methodOfTopDressingId'] != null) vg.methodOfTopDressingId = b['vg_methodOfTopDressingId'];
        if (b['vg_typeOfTopDressingFertilizerId'] != null) vg.typeOfTopDressingFertilizerId = b['vg_typeOfTopDressingFertilizerId'];
        if (b['vg_waterQualityEcId'] != null) vg.waterQualityEcId = b['vg_waterQualityEcId'];
        if (b['vg_thaLimeApplied'] != null && b['vg_thaLimeApplied'] !== '') (vg as any).tHaLimeApplied = Number(b['vg_thaLimeApplied']);
        if (b['vg_timeLimeApplied'] != null) vg.timeLimeApplied = b['vg_timeLimeApplied'];
        if (b['vg_fullBloomDate'] != null) vg.fullBloomDate = b['vg_fullBloomDate'];
        if (b['vg_rowSpacing'] != null && b['vg_rowSpacing'] !== '') vg.rowSpacing = Number(b['vg_rowSpacing']);
        if (b['vg_plantSpacing'] != null && b['vg_plantSpacing'] !== '') vg.plantSpacing = Number(b['vg_plantSpacing']);
        if (b['vg_replantDate'] != null) vg.replantDate = b['vg_replantDate'];
        if (b['vg_risingDate'] != null) vg.risingDate = b['vg_risingDate'];
        if (b['vg_plantsPerHectare'] != null && b['vg_plantsPerHectare'] !== '') vg.plantsPerHectare = Number(b['vg_plantsPerHectare']);
      }

      // Vineyard extras
      if (row.field.fieldType === 3 && row.extraDetail?.vineyard) {
        const vy = row.extraDetail.vineyard;
        if (b['vy_growthPower'] != null && b['vy_growthPower'] !== '') vy.growthPower = Number(b['vy_growthPower']);
        if (b['vy_fertilizerTypeId'] != null) vy.fertilizerTypeId = b['vy_fertilizerTypeId'];
        if (b['vy_plantYear'] != null) vy.plantYear = b['vy_plantYear'];
        if (b['vy_fullBloomDate'] != null) vy.fullBloomDate = b['vy_fullBloomDate'];
        if (b['vy_plantSpacing'] != null && b['vy_plantSpacing'] !== '') vy.plantSpacing = Number(b['vy_plantSpacing']);
        if (b['vy_rowSpacing'] != null && b['vy_rowSpacing'] !== '') vy.rowSpacing = Number(b['vy_rowSpacing']);
        if (b['vy_replantDate'] != null) vy.replantDate = b['vy_replantDate'];
        if (b['vy_budBreakDate'] != null) vy.budBreakDate = b['vy_budBreakDate'];
        if (b['vy_buddingDate'] != null) vy.buddingDate = b['vy_buddingDate'];
        if (b['vy_rootstock'] != null && b['vy_rootstock'] !== '') vy.rootstock = b['vy_rootstock'];
      }

      // Fruit extras
      if (row.field.fieldType === 4 && row.extraDetail?.fruit) {
        const fr = row.extraDetail.fruit;
        if (b['fr_growthPower'] != null && b['fr_growthPower'] !== '') fr.growthPower = Number(b['fr_growthPower']);
        if (b['fr_fertilizerTypeId'] != null) fr.fertilizerTypeId = b['fr_fertilizerTypeId'];
        if (b['fr_plantYear'] != null) fr.plantYear = b['fr_plantYear'];
        if (b['fr_fullBloomDate'] != null) fr.fullBloomDate = b['fr_fullBloomDate'];
        if (b['fr_plantSpacing'] != null && b['fr_plantSpacing'] !== '') fr.plantSpacing = Number(b['fr_plantSpacing']);
        if (b['fr_rowSpacing'] != null && b['fr_rowSpacing'] !== '') fr.rowSpacing = Number(b['fr_rowSpacing']);
        if (b['fr_replantDate'] != null) fr.replantDate = b['fr_replantDate'];
        if (b['fr_budBreakDate'] != null) fr.budBreakDate = b['fr_budBreakDate'];
        if (b['fr_buddingDate'] != null) fr.buddingDate = b['fr_buddingDate'];
        if (b['fr_rootstock'] != null && b['fr_rootstock'] !== '') fr.rootstock = b['fr_rootstock'];
        if (b['fr_plantsPerHectare'] != null && b['fr_plantsPerHectare'] !== '') fr.plantsPerHectare = Number(b['fr_plantsPerHectare']);
      }

      row.dirty = true;
      // The batch value was validated above, so this clears any stale flag on
      // rows that were previously invalid.
      row.cultivarSizeInvalid = this.cultivarSizeFormatError(row.field.cultivarSize) !== null;
    }

    this.batchValues = {};
    this.selectedRows = new Set();
  }

  clearBatch(): void {
    this.batchValues = {};
    this.selectedRows = new Set();
    this.batchCultivarSizeError = null;
  }

  clearBatchForTable(rows: TableRow[]): void {
    rows.forEach(r => this.selectedRows.delete(r.field.id));
    this.selectedRows = new Set(this.selectedRows);
    this.batchValues = {};
    this.batchCultivarSizeError = null;
  }
  showBatchExtras(tableFieldType: number): boolean {
    return !this.batchValues['fieldType'] || this.batchValues['fieldType'] === tableFieldType;
  }

  openCreateSamplesDialog(): void {
    const selectedFields = this.getSelectedRows().map(r => ({
      fieldId: r.field.fieldId,
      fieldName: r.field.fieldName,
      farmName: r.farmName
    }));
    this.batchDataService.setData(selectedFields, this.seasonId);
    this.router.navigate(['../batch-create-samples'], { relativeTo: this.route });
  }

  addDefinition(row: TableRow): void {
    const fieldId = row.fieldGroupId || row.field.fieldId;
    if (!fieldId) return;

    this.seasonManagerService.addDefinition(this.seasonId, fieldId, row.field).subscribe((newDef: any) => {
      const newRow: TableRow = {
        // Take cultivarSize and notes from the API response rather than the
        // source row. The row spread would otherwise show a copied cultivar
        // size that was never created, putting the field visibly over its
        // total against a definition the API stored as null.
        field: {
          ...row.field,
          id: newDef.id,
          cultivarSize: newDef.cultivarSize ?? null,
          notes: newDef.notes ?? null,
        },
        farmName: row.farmName,
        extraDetail: row.extraDetail ? { ...row.extraDetail } : null,
        dirty: false,
        definitionId: newDef.id,
        fieldGroupId: fieldId,
      };

      const insertIdx = this.allRows.indexOf(row) + 1;
      this.allRows.splice(insertIdx, 0, newRow);
      this.setDefinitionFlags();
      this.updateFilteredRows();
    });
  }

  deleteDefinition(row: TableRow): void {
    const fieldId = row.fieldGroupId || row.field.fieldId;
    if (!fieldId || (row.definitionCount || 0) <= 1) return;

    const dialogRef = this.matDialog.open(DraftConfirmationDialogComponent, {
      data: { dialogType: 'delete', dialogTitle: 'Delete Definition', dialogSubTitle: 'Are you sure you want to delete this crop definition?' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.seasonManagerService.deleteDefinition(row.definitionId || row.field.id).subscribe(() => {
        const idx = this.allRows.indexOf(row);
        if (idx > -1) this.allRows.splice(idx, 1);
        this.setDefinitionFlags();
        this.updateFilteredRows();
      });
    });
  }
}
